// Montage de la page des pistes sur le DOM. Exclu de la couverture de tests : tout ce qui se
// teste est dans controller.ts, view-model.ts et les composants.
import { html } from 'htm/preact';
import { render } from 'preact';
import { createSpeechSynthesis } from '../../adapters/speech/speech-synthesis.ts';
import { createLocalStorageNotebook, NOTEBOOK_KEY } from '../../adapters/storage/local-storage-notebook.ts';
import { createLocalStoragePreferences } from '../../adapters/storage/local-storage-preferences.ts';
import { safeStorage } from '../../adapters/storage/safe-storage.ts';
import { createMorphologyLoader, createNeuralTagging, createPhoneticsLoader, createScalesLoader, createVerbsLoader } from '../composition.ts';
import { App } from './app.ts';
import { downloadText } from './download.ts';
import { claimsSpace, createTracksController, type TracksState } from './controller.ts';
import { decodeFragment, isShareFragment } from './share-link.ts';
import { nextTheme, type Theme } from './components/theme-toggle.ts';

const root = document.getElementById('app')!;
// Cookies bloqués : lire `localStorage` lève ; la page démarre alors avec un carnet de séance.
const { storage, persistent } = safeStorage(() => localStorage);
const { tagger, preload } = createNeuralTagging();
// Le lien qui a ouvert la page : lu une fois, puis effacé, pour qu'un rechargement montre l'outil.
const fragment = location.hash;
if (isShareFragment(fragment)) history.replaceState(null, '', location.pathname + location.search);
// À l'ouverture de l'inspecteur, le focus y passe, pour que les flèches et Échap répondent.
let inspecting = false;
// À l'ouverture de la vue d'arrivée, le focus passe sur son titre.
let arriving = false;
const page = document.documentElement;
const onTheme = () => {
  page.dataset['theme'] = nextTheme(page.dataset['theme'] as Theme | undefined, matchMedia('(prefers-color-scheme: dark)').matches);
};
const draw = (state: TracksState) => {
  render(html`<${App} state=${state} controller=${controller} onTheme=${onTheme} version=${__OULIPAO_VERSION__} today=${new Date()} />`, root);
  if (state.selected !== undefined && !inspecting) root.querySelector<HTMLElement>('.inspector')?.focus();
  inspecting = state.selected !== undefined;
  if (state.arrival && !arriving) root.querySelector<HTMLElement>('#arrival-title')?.focus();
  arriving = state.arrival !== undefined;
};
const controller = createTracksController(
  {
    tagger,
    preload,
    loadMorphology: createMorphologyLoader(import.meta.url),
    loadVerbs: createVerbsLoader(import.meta.url),
    loadPhonetics: createPhoneticsLoader(import.meta.url),
    loadScales: createScalesLoader(import.meta.url),
    copy: (text) => navigator.clipboard.writeText(text),
    arrival: decodeFragment(fragment),
    // La voix du système : sans synthèse vocale dans le navigateur, pas d'écoute.
    ...('speechSynthesis' in window && { speech: createSpeechSynthesis(speechSynthesis, SpeechSynthesisUtterance) }),
    preferences: createLocalStoragePreferences(storage),
    notebook: {
      storage: createLocalStorageNotebook(storage),
      persistent,
      shareBase: location.origin + location.pathname,
      now: () => new Date(),
      newId: () => crypto.randomUUID(),
      confirm: (message) => window.confirm(message),
      // Un fichier proposé à l'enregistrement : rien ne quitte la machine.
      download: (name, text) =>
        downloadText(
          {
            createObjectURL: (blob) => URL.createObjectURL(blob),
            revokeObjectURL: (url) => URL.revokeObjectURL(url),
            link: (href, download) => Object.assign(document.createElement('a'), { href, download }),
          },
          name,
          text,
        ),
    },
  },
  draw,
);
draw(controller.state);

// Un autre onglet a écrit le carnet : celui-ci le relit (clé du carnet, ou stockage vidé).
addEventListener('storage', (event) => {
  if (event.key === NOTEBOOK_KEY || event.key === null) controller.syncNotebook();
});

// Les raccourcis de l'inspecteur et de l'écoute répondent où que soit le focus, sauf dans un champ de saisie.
const inField = (target: EventTarget | null) =>
  target instanceof HTMLElement && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable);
document.addEventListener('keydown', (event) => {
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (controller.shortcut(event.key, inField(event.target))) event.preventDefault();
});
// Sur un bouton qui a le focus, la barre d'espace l'activerait au relâché : elle sert à l'écoute,
// sauf sans voix française, où elle garde son effet ordinaire.
document.addEventListener('keyup', (event) => {
  if (event.key === ' ' && claimsSpace(controller.state, inField(event.target))) event.preventDefault();
});
// L'écoute se tait quand l'onglet est masqué ou la page quittée, et ne reprend pas seule.
document.addEventListener('visibilitychange', () => document.hidden && controller.stop());
addEventListener('pagehide', () => controller.stop());

// La grille compte ses pas par page d'après sa largeur ; la bande du texte résultant se colle
// en haut de l'écran dès que le repère placé juste au-dessus d'elle en sort.
new ResizeObserver(([entry]) => controller.resize(entry!.contentRect.width)).observe(root.querySelector('.rack')!);
// La bande collée se fait compacte : sur une page courte, la place perdue la rendrait trop courte pour
// défiler, le défilement reviendrait à zéro et la bande se décollerait aussitôt. On rend cette place
// en bas de page tant qu'elle est collée.
new IntersectionObserver(([entry]) => {
  // Avant la première mise en pistes, pas de bloc résultat : rien à réserver.
  const height = () => root.querySelector<HTMLElement>('.result')?.offsetHeight ?? 0;
  const before = height();
  // Réservée avant le rendu compact, sinon le navigateur ramène le défilement à zéro avant qu'on l'ajuste.
  document.body.style.paddingBottom = entry!.isIntersecting ? '' : `${before}px`;
  controller.pin(!entry!.isIntersecting);
  if (!entry!.isIntersecting) document.body.style.paddingBottom = `${Math.max(0, before - height())}px`;
}).observe(root.querySelector('.pin-sentinel')!);
